import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import {
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
  borderRadius,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import StewartInput from "controls/global/stewart-input";
import StewartAutocompleteWithCheckboxes, {
  OptionRenderType,
} from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import {
  PolicyManagementRecord,
} from "entities/UIModel/PolicyManagementRecord";
import { useColumnSettings } from "utils/custom-hooks/useColumnSettings";
import { useCountyList } from "utils/custom-hooks/useCountyList";
import { usePolicyTypeOptions } from "utils/custom-hooks/usePolicyTypeOptions";
import { useFormPrefixOptions } from "utils/custom-hooks/useFormPrefixOptions";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { usePropertyTypeOptions } from "utils/custom-hooks/usePropertyTypeOptions";
import { useAsyncTextFilter } from "utils/custom-hooks/useAsyncTextFilter";
import { useTransCodeOptions } from "utils/custom-hooks/useTransCodeOptions";
import { useDateTypeOptions } from "utils/custom-hooks/useDateTypeOptions";
import { usePolicyErrorTypeOptions } from "utils/custom-hooks/usePolicyErrorTypeOptions";
import { useStateList } from "utils/custom-hooks/useStateList";
import {
  getFormNameListByText,
  getFileNumberListByText,
  getOriginalPolicyNumberListByText,
} from "utils/api/lookupApi";
import PolicyStatsBar from "../shared/PolicyStatsBar";
import BulkActionBar, { BulkButton } from "../shared/BulkActionBar";
import AdvancedSearchButton from "../../utils/AdvancedSearchButton";
import AdvancedSearchPanel, { parseIsoDate } from "../../utils/AdvancedSearchPanel";
import ExportButton from "../shared/ExportButton";
import CloseQueueTable from "./CloseQueueTable";
import { buildCqColumns, CQ_GRID_NAME } from "./cqColumns";
import { PolicyManagementCounts } from "../counts/types";
import { ConfirmCloseModal } from "../modals";
import FileExportDialog from "controls/global/file-export-dialog";
import IosShareIcon from '@mui/icons-material/IosShare';
import { orderBy as _orderBy } from "lodash";
import { PIPActionType } from "utils/data/enum";
import { closePolicyTransactionBatch, exportClosePreviewReport, exportPolicyTransaction, getClosePreviewReport, PolicyClosePreviewSummaryDto, PolicyTransactionCriteriaDto, PolicyTransactionExportCriteriaDto } from "utils/api/policyTransactionApi";
import { StageType } from "utils/data/enum";
import BulkActionModal from "../shared/BulkActionModal";
import { showToast } from "controls/global/stewart-toast";
import { handleBulkReturnPolicyTransaction } from "../shared/returnQueue";
import useSelectedRecords from "../shared/useSelectedRecords";
import { getTimeZoneOffset } from "pages/utils/pageHelper";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const RequiredFilters = styled(Box)({
  display: "flex",
  gap: gaps.medium1,
  flexWrap: "wrap",
  alignItems: "flex-end",
  padding: padding.large1,
  backgroundColor: colors.white,
  borderRadius: borderRadius.small,
  border: `1px solid ${colors.grey09}`,
  "& .MuiInputBase-root": {
    height: 40,
    minHeight: 40,
  },
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 40,
  },
  "& .MuiAutocomplete-tag": {
    margin: "2px 4px",
    height: 24,
  },
});

const Field = styled(Box)({
  flex: "1 1 200px",
  minWidth: "180px",
  maxWidth: "260px",
});

const FieldLabel = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold1,
  color: colors.blue15,
  marginBottom: "4px",
});

const LabelSuffix = styled("span")({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.normal1,
  color: colors.grey11,
  marginLeft: gaps.xsmall2,
});

const RequiredStar = styled("span")({
  color: colors.red01,
});

const FieldActions = styled(Box)({
  display: "flex",
  gap: gaps.small1,
  marginLeft: "auto",
});

const ChipRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  flexWrap: "wrap",
});

const ChipsSeparator = styled(Box)({
  width: 1,
  height: 22,
  backgroundColor: colors.grey09,
  marginLeft: gaps.xsmall2,
  marginRight: gaps.xsmall2,
});

const Chip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: gaps.xsmall1,
  padding: `${padding.xsmall1} ${padding.medium1}`,
  borderRadius: borderRadius.xlarge1,
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  cursor: "pointer",
  userSelect: "none",
  border: `1px solid ${colors.grey09}`,
  backgroundColor: colors.white,
  color: colors.grey11,
  whiteSpace: "nowrap",
  "&.active": {
    backgroundColor: colors.blue08,
    color: colors.blue02,
    borderColor: colors.blue01,
  },
  "&.disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
    pointerEvents: "none",
  },
});

const ToolbarSpacer = styled(Box)({
  marginLeft: "auto",
  display: "flex",
  gap: gaps.small1,
  alignItems: "center",
});

const SelectedCount = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  color: colors.blue15,
  whiteSpace: "nowrap",
  marginLeft: gaps.small1,
});

type CqChip = "all" | "forcePromoted" | "edited" | "unedited";

interface CloseQueueTabProps {
  records: PolicyManagementRecord[];
  allRecords: PolicyManagementRecord[];
  onUpdateRecords: (updatedRecords: PolicyManagementRecord[]) => void;
  counts: PolicyManagementCounts;
  stateVals?: string[];
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  filters?: Record<string, string | string[] | number | null>;
  onFilterChange?: (name: string, value: string | string[] | number | null) => void;
  onResetFilters?: () => void;
  onChipCriteriaChange?: (criteria: Record<string, unknown>) => void;
  onAdvancedCriteriaChange?: (criteria: Record<string, unknown>) => void;
  onRequiredCriteriaChange?: (criteria: Record<string, unknown>) => void;
  applyEnabled?: boolean;
  onApplyFilters?: () => void;
  onRefresh?: () => void;
  sortColumn?: string;
  onSortChange?: (next: string) => void;
  userTimeZone?: string;
  // Global Search seed values to prefill matching column search boxes.
  headerFilterDefaults?: Record<string, string>;
}

function deriveCqChipCriteria(chips: Set<CqChip>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (chips.has("forcePromoted")) out.IsForcePromoted = 1;
  if (chips.has("edited") && !chips.has("unedited")) out.IsEdited = 1;
  if (chips.has("unedited") && !chips.has("edited")) out.IsEdited = 0;
  return out;
}

function deriveCqAdvancedCriteria(
  adv: Record<string, string | OptionRenderType[]>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const errors = adv.errorType;
  if (Array.isArray(errors) && errors.length > 0) {
    out.PolicyErrorTypeCodeList = errors.map((o) => o.value);
  }
  const dateType = typeof adv.dateType === "string" ? adv.dateType : "";
  if (dateType) out.DateTypeCode = dateType;
  const dateFrom = typeof adv.dateFrom === "string" ? adv.dateFrom : "";
  if (dateFrom) out.DateRangeStart = dateFrom;
  const dateTo = typeof adv.dateTo === "string" ? adv.dateTo : "";
  if (dateTo) out.DateRangeEnd = dateTo;
  return out;
}

const CQ_PROTECTED_FIELDS: (keyof PolicyManagementRecord)[] = ["uniqueIdentifier"];

const CloseQueueTab = ({
  records,
  allRecords,
  onUpdateRecords,
  counts,
  stateVals,
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  filters: filtersProp,
  onFilterChange,
  onResetFilters,
  onChipCriteriaChange,
  onAdvancedCriteriaChange,
  onRequiredCriteriaChange,
  applyEnabled,
  onApplyFilters,
  onRefresh,
  sortColumn,
  onSortChange,
  userTimeZone,
  headerFilterDefaults,
}: CloseQueueTabProps) => {
  const navigate = useNavigate();
  const [{ profileSettings }] = useProfileSettingsCache();
  const handleRowAction = useCallback(
    (record: PolicyManagementRecord) => {
      navigate(`/policyManagement/product-details/${record.uniqueIdentifier}`, {
        state: { record },
      });
    },
    [navigate]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Local, not lifted/persisted: a tab switch or chip click should always
  // collapse expanded rows, not preserve them.
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  // Column-header filters live in the loader; see filtersProp on props.
  void filtersProp;
  const [activeChips, setActiveChips] = useState<Set<CqChip>>(
    new Set<CqChip>(["all"])
  );
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  // Bumped on Reset so the table (and all its uncontrolled column-header
  // inputs) remount with fresh empty state.
  const [resetCounter, setResetCounter] = useState(0);
  const [advancedValues, setAdvancedValues] = useState<
    Record<string, string | OptionRenderType[]>
  >({});
  const [openFileExportModal, setOpenFileExportModal] = useState<boolean>(false);

  const handleAdvancedChange = useCallback(
    (key: string, value: string | OptionRenderType[]) => {
      setAdvancedValues((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const [selectedStates, setSelectedStates] = useState<OptionRenderType[]>([]);
  const [receivedThrough, setReceivedThrough] = useState<string>("");
  const [bulkActionType, setBulkActionType] = useState<PIPActionType>(PIPActionType.BULK_HOLD);
  const [openBulkActionModal, setOpenBulkActionModal] = useState<boolean>(false);

  // MM/YYYY derived from the Received Through date.
  const accountingPeriod = useMemo(() => {
    if (!receivedThrough) return "";
    const d = new Date(receivedThrough);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  }, [receivedThrough]);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // "all" closes every record in the queue (requires typing the accounting
  // period to confirm); "selected" closes only the checked rows.
  const [closeMode, setCloseMode] = useState<"all" | "selected">("all");
  const [closePreview, setClosePreview] = useState<PolicyClosePreviewSummaryDto | null>(null);
  const [closePreviewLoading, setClosePreviewLoading] = useState(false);
  const [closePreviewError, setClosePreviewError] = useState(false);

  const requiredFiltersSet =
    selectedStates.length > 0 && receivedThrough !== "";

  const { stateList } = useStateList();
  // Bumped when stateVals changes so the State autocomplete remounts with the
  // synced selection (its internal state doesn't react to defaultSelectedOption
  // prop changes otherwise).
  const [stateSyncCounter, setStateSyncCounter] = useState(0);

  const stateOptions = useMemo(() => {
    if (stateVals && stateVals.length > 0) {
      return (stateList ?? []).filter((s) => stateVals.includes(s.value));
    }
    return stateList ?? [];
  }, [stateVals, stateList]);

  // Auto-mirror the page-level State picker into CQ when it has values; leave
  // CQ's selection untouched when the page-level is cleared.
  useEffect(() => {
    if (stateVals && stateVals.length > 0) {
      setSelectedStates(
        (stateList ?? []).filter((s) => stateVals.includes(s.value))
      );
      setStateSyncCounter((c) => c + 1);
    }
  }, [stateVals, stateList]);

  // Chips, column-header filters, required filters and advanced search all drive
  // the server criteria — nothing to filter client-side. A client filter would
  // also be wrong under server-side paging: it would trim only the current page
  // while the footer still reports whole-query totals.
  const filteredRecords = records;

  useEffect(() => {
    onChipCriteriaChange?.(deriveCqChipCriteria(activeChips));
  }, [activeChips, onChipCriteriaChange]);

  useEffect(() => {
    const next: Record<string, unknown> = {};
    // Key names must match PolicyTransactionCriteria exactly — System.Text.Json
    // silently drops unknown properties, so a typo here is a filter that never
    // reaches the SP. `s.value` is the state abbreviation, which is what
    // StateAbbrList expects.
    if (selectedStates.length > 0)
      next.StateAbbrList = selectedStates.map((s) => s.value);
    if (receivedThrough) {
      next.ReceivedDateEnd = `${receivedThrough}T00:00:00`;
      // YYYY-MM-DD → YYYYMM
      next.AccountingPeriod = receivedThrough.slice(0, 7).replace("-", "");
    }
    onRequiredCriteriaChange?.(next);
  }, [selectedStates, receivedThrough, onRequiredCriteriaChange]);

  useEffect(() => {
    onAdvancedCriteriaChange?.(deriveCqAdvancedCriteria(advancedValues));
  }, [advancedValues, onAdvancedCriteriaChange]);

  const handleToggleRow = useCallback((record: PolicyManagementRecord) => {
    if (record.isLocked) return;
    setSelectedIds((prev) =>
      prev.includes(record.uniqueIdentifier)
        ? prev.filter((id) => id !== record.uniqueIdentifier)
        : [...prev, record.uniqueIdentifier]
    );
  }, []);

  const handleToggleAllRows = useCallback(() => {
    const selectableIds = filteredRecords
      .filter((r) => !r.isLocked)
      .map((r) => r.uniqueIdentifier);

    const prevSelected = new Set(selectedIds);
    const deselectingPage =
      selectableIds.length > 0 &&
      selectableIds.every((id) => prevSelected.has(id));

    setSelectedIds((prev) => {
      if (deselectingPage) {
        const pageIds = new Set(selectableIds);
        return prev.filter((id) => !pageIds.has(id));
      }
      return [...prev, ...selectableIds.filter((id) => !prevSelected.has(id))];
    });
  }, [filteredRecords, selectedIds]);

  const handleFiltersChange = useCallback(
    (name: string, value: string | number | null) => {
      onFilterChange?.(name, value);
    },
    [onFilterChange]
  );

  const handleMultiSelectChange = useCallback(
    (
      e: { target: { name: string; value: OptionRenderType[] } },
      value: OptionRenderType[]
    ) => {
      const fieldName = e.target.name;
      const selectedValues = value.map((v) => v.value);
      onFilterChange?.(
        fieldName,
        selectedValues.length > 0 ? selectedValues : null
      );
    },
    [onFilterChange]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const toggleChip = useCallback((chip: CqChip) => {
    setActiveChips(new Set<CqChip>([chip]));
    setExpandedRowIds([]);
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetFilters?.();
    setSelectedIds([]);
    setExpandedRowIds([]);
    setActiveChips(new Set<CqChip>(["all"]));
    setSelectedStates([]);
    setReceivedThrough("");
    setAdvancedValues({});
    setResetCounter((c) => c + 1);
  }, [onResetFilters]);

  const selectedCount = selectedIds.length;
  // Locked records are excluded from selection; surface the skipped count.
  const lockedNotSelectedCount = useMemo(
    () => filteredRecords.filter((r) => r.isLocked).length,
    [filteredRecords]
  );

  const selectedRecords = useSelectedRecords(filteredRecords, selectedIds);

  const openConfirmClose = useCallback(
    async (mode: "all" | "selected") => {
      const toClose = mode === "all" ? filteredRecords : selectedRecords;
      if (toClose.length === 0) return;
      setCloseMode(mode);
      setClosePreview(null);
      setClosePreviewError(false);
      setClosePreviewLoading(true);
      // Fetch a fresh, server-computed summary for exactly the records that
      // will be closed (same PolicyTransactionIDList handleConfirmClose uses)
      // instead of recomputing totals from possibly-stale client state. The
      // modal only opens once this resolves, so it never renders mid-load.
      const preview = await getClosePreviewReport({
        UserTimeZone: userTimeZone ?? "",
        StageTypeCode: StageType.CLOSE,
        PolicyTransactionIDList: toClose.map((r) => r.PolicyTransactionID).join(","),
        ListCount: toClose.length,
      });
      setClosePreviewLoading(false);
      if (preview) {
        setClosePreview(preview);
        setConfirmCloseOpen(true);
      } else {
        setClosePreviewError(true);
        showToast({ type: "error", message: "Failed to load the close preview. Please try again." });
      }
    },
    [filteredRecords, selectedRecords, userTimeZone]
  );

  const handleConfirmClose = useCallback(async () => {
    const toClose = closeMode === "all" ? filteredRecords : selectedRecords;
    if (toClose.length === 0) return;

    // "selected": close exactly the checked rows by ID, as before.
    // "all": close every record matching the page's State + Received Through
    // filters, computed server-side — not limited to whatever's loaded
    // client-side in filteredRecords.
    const result =
      closeMode === "selected"
        ? await closePolicyTransactionBatch({
            UserTimeZone: userTimeZone ?? "",
            StageTypeCode: StageType.CLOSE,
            PolicyTransactionIDList: toClose.map((r) => r.PolicyTransactionID).join(","),
            ListCount: toClose.length,
          })
        : await closePolicyTransactionBatch({
            // StateAbbrList, not StateCodeList: this posts to the same
            // PolicyTransactionCriteria model, which has no StateCodeList
            // property — the old key was dropped at model binding, so "Close
            // All" ignored the State filter entirely.
            StateAbbrList: selectedStates.map((s) => s.value).join(","),
            ReceivedDateEnd: `${receivedThrough}T00:00:00${getTimeZoneOffset(profileSettings)}`,
          });
    if (result) {
      // Optimistic local update so the grid reflects the close immediately;
      // onRefresh then re-syncs with the server's authoritative record set.
      onUpdateRecords(toClose.map((r) => ({ ...r, isClosed: true })));
      onRefresh?.();
      clearSelection();
    } else {
      showToast({ type: "error", message: "Failed to close the selected records." });
    }
  }, [
    closeMode,
    filteredRecords,
    selectedRecords,
    onUpdateRecords,
    onRefresh,
    userTimeZone,
    selectedStates,
    receivedThrough,
    profileSettings,
    clearSelection,
  ]);

  const handleExportPreview = useCallback(async () => {
    const toClose = closeMode === "all" ? filteredRecords : selectedRecords;
    if (toClose.length === 0) return;
    const exported = await exportClosePreviewReport({
      UserTimeZone: userTimeZone ?? "",
      StageTypeCode: StageType.CLOSE,
      PolicyTransactionIDList: toClose.map((r) => r.PolicyTransactionID).join(","),
      ListCount: toClose.length,
    });
    if (!exported) {
      showToast({ type: "error", message: "Failed to export the close preview report." });
    }
  }, [closeMode, filteredRecords, selectedRecords, userTimeZone]);

  // ---- Column state lifted from CloseQueueTable ----
  const agentOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(filteredRecords.map((r) => r.AgentID ?? "").filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [filteredRecords]);

  const productTypeOptions = usePolicyTypeOptions();
  const formPrefixOptions = useFormPrefixOptions();
  const transCodeOptions = useTransCodeOptions();
  const policyFormNameAsync = useAsyncTextFilter(getFormNameListByText);
  const fileNumberAsync = useAsyncTextFilter(getFileNumberListByText);
  const originalPolicyNumberAsync = useAsyncTextFilter(getOriginalPolicyNumberListByText);

  const recordTypeOptions = useRecordTypeOptions();

  const propertyTypeOptions = usePropertyTypeOptions();

  const countyOptions = useCountyList(stateVals);

  const policyErrorTypeOptions = usePolicyErrorTypeOptions();

  const dateTypeOptions = useDateTypeOptions();

  const advancedSections = useMemo(
    () => [
      {
        title: "Filters",
        fields: [
          {
            key: "errorType",
            label: "Error Types",
            type: "multi" as const,
            options: policyErrorTypeOptions,
            inline: true,
          },
        ],
      },
      {
        title: "Date",
        fields: [
          {
            key: "dateType",
            label: "Date Type",
            type: "select" as const,
            options: dateTypeOptions,
            inline: true,
          },
          {
            key: "dateFrom",
            label: "",
            type: "date" as const,
            maxDate: parseIsoDate(advancedValues.dateTo),
          },
          { key: "dateRangeSeparator", label: "—", type: "separator" as const },
          {
            key: "dateTo",
            label: "",
            type: "date" as const,
            minDate: parseIsoDate(advancedValues.dateFrom),
          },
        ],
      },
    ],
    [
      policyErrorTypeOptions,
      dateTypeOptions,
      advancedValues.dateFrom,
      advancedValues.dateTo,
    ]
  );

  const defaultColumns = useMemo(
    () =>
      buildCqColumns({
        agentOptions,
        stateOptions,
        productTypeOptions,
        recordTypeOptions,
        propertyTypeOptions,
        countyOptions,
        formPrefixOptions,
        transCodeOptions,
        policyFormNameAsync,
        fileNumberAsync,
        originalPolicyNumberAsync,
        onFiltersChange: handleFiltersChange,
        onMultiSelectChange: handleMultiSelectChange,
        onRowAction: handleRowAction,
        headerFilterDefaults,
      }),
    [
      agentOptions,
      stateOptions,
      productTypeOptions,
      recordTypeOptions,
      propertyTypeOptions,
      countyOptions,
      formPrefixOptions,
      transCodeOptions,
      policyFormNameAsync,
      fileNumberAsync,
      originalPolicyNumberAsync,
      handleFiltersChange,
      handleMultiSelectChange,
      handleRowAction,
      headerFilterDefaults,
    ]
  );

  const {
    columns,
    hiddenColumns,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  } = useColumnSettings<PolicyManagementRecord>(
    CQ_GRID_NAME,
    defaultColumns,
    CQ_PROTECTED_FIELDS
  );

  // The Actions column is non-draggable and must always render last. Stale
  // server-saved column prefs can carry an old position for it (from before the
  // financial columns existed), which mergeOverrides would honor — so pin it to
  // the end here regardless of the saved/default position.
  const orderedColumns = useMemo(() => {
    const actionIdx = columns.findIndex((c) => c.field === "uniqueIdentifier");
    if (actionIdx < 0 || actionIdx === columns.length - 1) return columns;
    const next = [...columns];
    const [actionCol] = next.splice(actionIdx, 1);
    next.push(actionCol);
    return next;
  }, [columns]);

  const chooserOptions = useMemo<MenuColumnItem<PolicyManagementRecord>[]>(
    () =>
      defaultColumns
        .filter((c) => c.field !== "uniqueIdentifier")
        .map((c) => ({
          field: c.field,
          name: c.name,
          checked: !hiddenColumns.includes(c.field),
        })),
    [defaultColumns, hiddenColumns]
  );

  const handleColumnChecked = useCallback(
    (field: keyof PolicyManagementRecord, wasChecked: boolean) => {
      setColumnHidden(field, wasChecked);
    },
    [setColumnHidden]
  );


  const exportedColumns = useMemo(
    () => _orderBy(columns.filter(c => !hiddenColumns.includes(c.field)), "position").map(c => c.field),
    [columns, hiddenColumns]
  );
  const buildExportPayload = useCallback(
    (exportAll: boolean): PolicyTransactionExportCriteriaDto => {
      const baseCriteria: Partial<PolicyTransactionCriteriaDto> = {
        UserTimeZone: userTimeZone,
        RequestedPage: page,
        StageTypeCode: StageType.CLOSE,
        SortColumn: "receivedDate",
      };

      const searchCriteria = exportAll
        ? {
          ...baseCriteria,
          ListCount: 0,
          PolicyTransactionIDList: null,
        }
        : {
          ...baseCriteria,
          ListCount: selectedIds.length,
          PolicyTransactionIDList: selectedIds.join(", "),
        };

      return {
        ExportColumns: exportedColumns,
        SearchCriteria: searchCriteria,
      };
    },
    [exportedColumns, selectedIds, userTimeZone, page]
  );
  const handleFileExport = async () => {
    if (selectedIds.length > 0) {
      setOpenFileExportModal(true);
    } else {
      const payload: PolicyTransactionExportCriteriaDto = buildExportPayload(true);
      await exportPolicyTransaction(payload);
    }
  };

  const chipDefs: { key: CqChip; label: string; group: 1 | 2 | 3 }[] = [
    { key: "all", label: `All (${counts.cqAll})`, group: 1 },
    {
      key: "forcePromoted",
      label: `Force promoted (${counts.cqForcePromoted})`,
      group: 2,
    },
    { key: "edited", label: `Edited (${counts.cqEdited})`, group: 3 },
    { key: "unedited", label: `Unedited (${counts.cqUnedited})`, group: 3 },
  ];

  return (
    <TabContainer>
      <RequiredFilters>
        <Field>
          <FieldLabel>Accounting Period <LabelSuffix>(auto)</LabelSuffix></FieldLabel>
          <StewartInput
            name="accountingPeriod"
            id="accountingPeriod"
            label=""
            value={accountingPeriod}
            disabled
            fullWidth
          />
        </Field>
        <Field>
          <FieldLabel>State <RequiredStar>*</RequiredStar> <LabelSuffix>(scoped)</LabelSuffix></FieldLabel>
          <StewartAutocompleteWithCheckboxes
            key={stateSyncCounter}
            name="stateCode"
            hideLabel
            placeholder="Select"
            options={stateOptions}
            defaultSelectedOption={selectedStates}
            onChange={(_e, value) => setSelectedStates(value)}
            enableClearable
          />
        </Field>
        <Field>
          <FieldLabel>Received Through <RequiredStar>*</RequiredStar> <LabelSuffix>(drives period)</LabelSuffix></FieldLabel>
          <StewartInput
            name="receivedThrough"
            id="receivedThrough"
            label=""
            type="date"
            value={receivedThrough}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setReceivedThrough(e.target.value)
            }
            fullWidth
          />
        </Field>
        <FieldActions>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="primary"
            disabled={!requiredFiltersSet}
            onClick={onApplyFilters}
          >
            Apply Filters
          </ActionButton>
        </FieldActions>
      </RequiredFilters>

      <ChipRow>
        {chipDefs.map((c, idx) => {
          const prev = chipDefs[idx - 1];
          const sep = prev && prev.group !== c.group;
          return (
            <React.Fragment key={c.key}>
              {sep && <ChipsSeparator />}
              <Chip
                className={[
                  activeChips.has(c.key) ? "active" : "",
                  !requiredFiltersSet ? "disabled" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={
                  requiredFiltersSet ? () => toggleChip(c.key) : undefined
                }
              >
                {c.label}
              </Chip>
            </React.Fragment>
          );
        })}
        {selectedCount > 0 && (
          <SelectedCount>
            {selectedCount} record{selectedCount !== 1 ? "s" : ""} selected
            {lockedNotSelectedCount > 0 &&
              `, ${lockedNotSelectedCount} locked, not selected`}
          </SelectedCount>
        )}
        <ToolbarSpacer>
          <ActionButton
            variant="outlined"
            color="primary"
            disabled={filteredRecords.length === 0 || closePreviewLoading}
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => openConfirmClose("all")}
          >
            Close
          </ActionButton>
          <AdvancedSearchButton
            open={advancedSearchOpen}
            onClick={() => setAdvancedSearchOpen((o) => !o)}
            disabled={!requiredFiltersSet}
          />
          <Box sx={{ "& .MuiButton-root": { textTransform: "none" } }}>
            <ColumnsConfig
              options={chooserOptions}
              onChecked={handleColumnChecked}
              onShowAll={showAllColumns}
              onHideAll={hideAllColumns}
            />
          </Box>
          <ExportButton onClick={handleFileExport} />
        </ToolbarSpacer>
      </ChipRow>

      <AdvancedSearchPanel
        open={advancedSearchOpen}
        sections={advancedSections}
        values={advancedValues}
        onChange={handleAdvancedChange}
        onClear={() => setAdvancedValues({})}
      />

      <BulkActionBar open={selectedCount > 0} count={selectedCount}>
        <BulkButton
          icon={<CheckCircleOutlineIcon />}
          label="Close Selected"
          primary
          disabled={selectedCount === 0 || closePreviewLoading}
          onClick={() => openConfirmClose("selected")}
        />
        <BulkButton icon={<ReplyIcon />} label="Return to Pre-Close" onClick={()=>{setBulkActionType(PIPActionType.BULK_RETURN); setOpenBulkActionModal(true);}} />
      </BulkActionBar>

      <CloseQueueTable
        key={resetCounter}
        records={filteredRecords}
        selectedIds={selectedIds}
        onToggleRow={handleToggleRow}
        onToggleAllRows={handleToggleAllRows}
        columns={orderedColumns}
        hiddenColumns={hiddenColumns}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        onPageChange={onPageChange}
        totalRows={totalRows}
        totalPages={totalPages}
        isLoading={isLoading}
        sortColumn={sortColumn}
        onSortChange={onSortChange}
        onRefresh={onRefresh}
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        summaryComponent={
          <PolicyStatsBar records={filteredRecords} variant="cq" />
        }
      />

      <ConfirmCloseModal
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        records={closeMode === "all" ? filteredRecords : selectedRecords}
        accountingPeriod={accountingPeriod}
        requireAccountingPeriod={closeMode === "all"}
        onConfirm={handleConfirmClose}
        onExportPreview={handleExportPreview}
        summary={closePreview}
        summaryLoading={closePreviewLoading}
        summaryError={closePreviewError}
      />
      <FileExportDialog
        open={openFileExportModal}
        onExport={async (exportAll) => {
          setOpenFileExportModal(false);
          const payload = buildExportPayload(exportAll);
          await exportPolicyTransaction(payload);
        }}
        onCancel={() => setOpenFileExportModal(false)}
        showSelectionPrompt={selectedIds?.some((s) => s)}
        selectionPrompt={"Include only the selected policies or all policies?"}
        icon={IosShareIcon}
      />
      <BulkActionModal
        open={openBulkActionModal}
        onClose={() => setOpenBulkActionModal(false)}
        recordCount={selectedIds.length}
        tabName="CLOSE QUEUE"
        onConfirm={(reason) => handleBulkReturnPolicyTransaction(selectedIds, reason, StageType.PRECLOSE).then((ok) => {
          if (ok) {
            setOpenBulkActionModal(false);
            onRefresh?.();
            clearSelection();
          }
        })}
        returnTabName="PRE CLOSE"
        actionType={bulkActionType}
      />
    </TabContainer>
  );
};

export default CloseQueueTab;
